import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import LeadForm from "@/components/feature/LeadForm";
import { useJsonLd, usePageMeta } from "@/hooks/usePageMeta";
import { siteInfo, testimonials } from "@/mocks/site";

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const target = document.getElementById(location.hash.replace("#", ""));
      if (target) {
        window.requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }, [location.hash]);

  usePageMeta({
    title: "关于我们 - 吃瓜深扒社的内容原则与核对方法 | 吃瓜深扒社",
    description:
      "吃瓜深扒社专注选美赛事深度解析，坚持先验证再表达、区分事实与推测、保护信源并允许纠错。了解我们的定位、内容原则与线索投递方式。",
    keywords: "吃瓜深扒社,选美吃瓜,选美吃瓜黑幕,内容原则,线索投递",
    path: "/about",
  });

  useJsonLd("about", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": "https://dstcm.cn/about#aboutpage",
        url: "https://dstcm.cn/about",
        name: "关于吃瓜深扒社",
        inLanguage: "zh-CN",
        description:
          "了解吃瓜深扒社的品牌定位、发展理念、内容原则与联系方式。",
        mainEntity: { "@id": "https://dstcm.cn/#organization" },
      },
      {
        "@type": "Organization",
        "@id": "https://dstcm.cn/#organization",
        name: "吃瓜深扒社",
        url: "https://dstcm.cn/",
        email: "contact@chiguashenba.com",
        description:
          "专注选美赛事深度解析的中文内容站，把评分口径、评委构成与计分流程摆上台面。",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://dstcm.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://dstcm.cn/about" },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "关于我们" },
          ]}
        />

        <section className="relative w-full overflow-hidden border-b border-background-200">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20open%20notebooks%2C%20score%20sheets%20and%20a%20brass%20pen%20by%20a%20window%2C%20soft%20morning%20light%2C%20amber%20and%20cream%20tones%2C%20documentary%20photography%2C%20no%20people&width=1600&height=700&seq=pageant-about-banner&orientation=landscape"
              alt="吃瓜深扒社编辑整理评分表的工作场景"
              title="吃瓜深扒社 - 内容原则与核对方法"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/85 to-background-50/40"></div>
          <div className="relative mx-auto w-full px-4 py-14 md:px-6 md:py-20">
            <span className="flex items-center gap-2 font-label text-xs tracking-[0.2em] text-primary-600">
              <i className="ri-team-line" aria-hidden="true"></i>
              ABOUT US
            </span>
            <h1 className="mt-4 max-w-3xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              我们只做一件事：让赛果争议可以被核对
            </h1>
            <p className="mt-5 max-w-3xl text-sm leading-loose text-foreground-800 md:text-base">
              {siteInfo.intro}
            </p>
            <dl className="mt-10 grid grid-cols-2 gap-6 md:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="border-l-2 border-primary-300 pl-4">
                  <dt className="font-heading text-2xl font-bold text-primary-600">{stat.value}</dt>
                  <dd className="mt-1 text-xs text-foreground-600 md:text-sm">{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                网站定位
              </h2>
              <p className="mt-5 text-sm leading-loose text-foreground-800 md:text-base">
                {siteInfo.positioning}
              </p>
              <p className="mt-4 text-sm leading-loose text-foreground-700">
                我们不做转发式的爆料合集，也不靠情绪化标题带节奏。每一条关于选美吃瓜内定与黑幕的说法，都要能落到具体的时间、文件或数据上，否则它只会被标注为待验证线索，而不是结论。
              </p>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">发展理念</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  赛事要取信于人，靠的不是声明，而是把过程公开。我们做的事情，是替读者把那些本该公开、却常常缺席的部分补齐。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                四条内容原则
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {siteInfo.principles.map((principle) => (
                  <div key={principle.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className={`${principle.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                      {principle.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{principle.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              一路走来的几个节点
            </h2>
            <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-4">
              {siteInfo.timeline.map((item, index) => (
                <li key={item.year} className="relative rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 font-heading text-xs font-bold text-background-50">
                    {index + 1}
                  </span>
                  <p className="mt-4 font-heading text-xl font-black text-accent-600">{item.year}</p>
                  <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.id} className="flex h-full flex-col rounded-lg border border-background-200 bg-secondary-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-400" aria-hidden="true"></i>
                <blockquote className="mt-4 flex-1 text-sm leading-loose text-foreground-800">
                  {item.text}
                </blockquote>
                <figcaption className="mt-5 border-t border-secondary-200 pt-4">
                  <span className="block font-heading text-sm font-bold text-foreground-950">{item.name}</span>
                  <span className="mt-1 block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" className="scroll-mt-24 border-t border-background-200 bg-background-100">
          <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-14 md:grid-cols-2 md:px-6 md:py-20">
            <div>
              <span className="flex items-center gap-2 font-label text-xs tracking-[0.2em] text-primary-600">
                <i className="ri-contacts-book-2-line" aria-hidden="true"></i>
                CONTACT
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                订阅更新与线索投递
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700">
                订阅后你会收到新一期赛果解析；投递线索时，请尽量附上可核查的公开物料。
              </p>

              <dl className="mt-8 space-y-5">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                  </span>
                  <div>
                    <dt className="font-label text-sm text-foreground-600">邮箱</dt>
                    <dd className="mt-1">
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-900 hover:text-primary-600">
                        {siteInfo.email}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-time-line text-lg" aria-hidden="true"></i>
                  </span>
                  <div>
                    <dt className="font-label text-sm text-foreground-600">回复说明</dt>
                    <dd className="mt-1 text-sm text-foreground-900">{siteInfo.workingNote}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-shield-user-line text-lg" aria-hidden="true"></i>
                  </span>
                  <div>
                    <dt className="font-label text-sm text-foreground-600">信息保护</dt>
                    <dd className="mt-1 text-sm text-foreground-900">
                      投递者的身份信息不对外公开，物料引用会先做脱敏处理。
                    </dd>
                  </div>
                </div>
              </dl>
            </div>

            <LeadForm />
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}